import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store"; // Import RootState
import { AuthState, AuthResponse } from "./types/auth-types";
import { ApiError } from "./types/common-types";

// Set initial state
const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  error: null,
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// **Login Thunk**
export const login = createAsyncThunk<
  AuthResponse,
  { identifier: string; password: string },
  { rejectValue: ApiError }
>("auth/login", async (credentials, thunkAPI) => {
  try {
    const response = await axios.post(`${API_URL}/admin-login`, {
      ...credentials,
    });
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to login"
    );
  }
});

// **Signup Thunk**
export const signup = createAsyncThunk<
  AuthResponse,
  { email: string; password: string; username: string },
  { rejectValue: ApiError }
>("auth/signup", async (credentials, thunkAPI) => {
  try {
    const response = await axios.post(`${API_URL}/signup`, credentials);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to register"
    );
  }
});

// **Change Password Thunk**
export const changePassword = createAsyncThunk<
  void,
  { oldPassword: string; newPassword: string },
  { rejectValue: ApiError; state: RootState }
>("auth/changePassword", async ({ oldPassword, newPassword }, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

    await axios.post(
      `${API_URL}/change-password`,
      { oldPassword, newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to change password"
    );
  }
});

// **Change Email Thunk**
export const changeEmail = createAsyncThunk<
  { user: any }, // Success return type (updated user data)
  { email: string; password: string }, // Argument type
  { rejectValue: ApiError; state: RootState }
>("auth/changeEmail", async ({ email, password }, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

    const response = await axios.post(
      `${API_URL}/change-email`,
      { email, password },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to change email"
    );
  }
});

// **Change Username Thunk**
export const updatePersonalInfo = createAsyncThunk<
  { user: any },
  {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    country?: string;
    preferredLanguage?: string;
  },
  { rejectValue: { message: string }; state: RootState }
>("auth/updatePersonalInfo", async (data, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/update-profile`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data || {
        message: "Failed to update personal information",
      }
    );
  }
});

// **Auth Slice**
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.loading = false;
      state.error = null;
      // Clear token from storage
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
    },
  },
  extraReducers: (builder) => {
    builder
      // Login Cases
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to login";
      })

      // Signup Cases
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to register";
      })

      // Change Password Cases
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to change password";
      })

      // Change Email Cases
      .addCase(changeEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user; // Update user with new email
      })
      .addCase(changeEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to change email";
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
